import { Button, Icon, Unifree } from '@yes.technology/react-toolkit'
import { FocusEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import { formatFileSize } from 'modules/shared/utils/formatFileSize'
import { fileToBase64 } from 'utils/file/file'
import FileUploader from '../../../shared/FileUploader'
import useDocument from '../../../shared/hooks/useDocument'
import useModal from '../../../shared/hooks/useModal'
import usePermission from '../../../shared/hooks/usePermission'
import PermissionDeniedCard from '../../../shared/PermissionDeniedCard'
import VideoView from '../../../shared/VideoView'
import WebcamStreamCapture from '../../../shared/WebcamStreamCapture'
import Player from '../../../shared/WebcamStreamCapture/components/Player'
import useUploadS3, {
  UploadFileResult
} from '../../hooks/useUploadS3/useUploadS3'
import { useAppDispatch } from 'state/store'
import { startLoading, stopLoading } from 'modules/interaction/redux'
import useBlueprintsContext from 'modules/interaction/hooks/useBlueprintsContext'

type ActivityDocumentVideoProps = {
  uuid?: string
  inputValue: string
  required?: boolean
  onChange: (value: string) => void
  label: string
}

type Values = {
  uuid?: string
  uuid_status?: string
  statusDes?: string
  contentType?: string
  size?: string
  url?: string
  des?: string
  file?: string
}

const ActivityDocumentVideo = ({
  uuid,
  inputValue,
  onChange,
  label,
  required
}: ActivityDocumentVideoProps) => {
  const { t } = useTranslation()
  const { createOrUpdateDocument } = useDocument()
  const { isOpenModal, openModal, closeModal } = useModal()
  const { permissionDenied, deniedType, requestAccess, clearPermissionDenied } =
    usePermission('camera-microphone')
  const { uploadFile } = useUploadS3()

  const dispatch = useAppDispatch()
  const { executeBlueprints } = useBlueprintsContext()

  const [view, setView] = useState(false)
  const [values, setValues] = useState<Values>()

  useEffect(() => {
    if (inputValue !== '') {
      setValues(JSON.parse(inputValue))
    }
  }, [inputValue])

  const startLoadingPage = () => {
    dispatch(
      startLoading({
        des: t('file-uploader.loading.des'),
        complement: t('file-uploader.loading.complement')
      })
    )
  }

  const stopLoadingPage = () => {
    dispatch(stopLoading())
  }

  const onSuccessUploadFile = async ({ url, content }: UploadFileResult) => {
    const values = inputValue && JSON.parse(inputValue)
    const { document, statusDes, statusUuid } = await createOrUpdateDocument({
      uuid: values?.uuid,
      content_type: content.type,
      url,
      complement: JSON.stringify({ size: content.size })
    })

    const value = JSON.stringify({
      ...values,
      uuid: document?.uuid,
      uuid_status: statusUuid,
      statusDes: statusDes,
      des: document?.des,
      contentType: content.type,
      size: content.size,
      url,
      file: await fileToBase64(content)
    })

    onChange(value)

    executeBlueprints({
      type: 'activity_changed',
      activityValue: value,
      activityModelUuid: uuid
    })
    stopLoadingPage()
  }

  const captureVideo = (videoUrl: string) => {
    startLoadingPage()

    // close camera
    closeModal()

    uploadFile({
      file: videoUrl,
      fileType: 'blob'
    })
      .then(onSuccessUploadFile)
      .catch(stopLoadingPage)
  }

  const onChangeDescription = (value: string) => {
    onChange(
      JSON.stringify({
        ...values,
        des: value
      })
    )
  }

  const saveDescription = (event: FocusEvent<HTMLInputElement>) => {
    if (!inputValue || !values?.file) {
      return
    }

    // workaround to solve a react issue when an event triggers a re-render the other events don't fire. As discussed this workaround should be deleted in a future refactor
    setTimeout(async () => {
      const values = JSON.parse(inputValue)
      const { document, statusDes, statusUuid } = await createOrUpdateDocument(
        {
          uuid: values.uuid,
          des: event.target.value
        },
        false
      )

      onChange(
        JSON.stringify({
          ...values,
          uuid: document?.uuid,
          des: event.target.value,
          uuid_status: statusUuid,
          statusDes: statusDes
        })
      )
    }, 500)
  }

  const uploadLocalFile = (file: File) => {
    startLoadingPage()

    uploadFile({
      file
    })
      .then(onSuccessUploadFile)
      .catch(stopLoadingPage)
  }

  const clearData = () => {
    onChange('')
    setValues({})
    executeBlueprints({
      type: 'activity_changed',
      activityValue: '',
      activityModelUuid: uuid
    })
  }

  const closeWebcam = () => {
    closeModal()
  }

  const permissionDeniedTitle =
    deniedType === 'camera-microphone'
      ? t('permission.denied.camera-microphone-title')
      : deniedType === 'microphone'
        ? t('permission.denied.microphone-title')
        : t('permission.denied.camera-title')

  const handleCameraClick = async () => {
    const granted = await requestAccess()
    if (granted) openModal()
  }

  return (
    <>
      {!isOpenModal && (
        <div style={{ display: 'grid', rowGap: '8px' }}>
          <Unifree
            label={t('activity-document-video.labels.des')}
            value={values?.des || ''}
            onChange={onChangeDescription}
            onBlur={saveDescription}
            data-testid='description-field'
            disabled={!values?.file}
            placeholder={t('activity-document-video.placeholders.des')}
          />
          <div
            className='d-grid align-items-end'
            style={{ gridTemplateColumns: '1fr auto auto' }}
          >
            <div className='flex-grow-1' style={{ marginRight: '8px' }}>
              <Unifree
                label={label || t('activity-document-video.labels.uuid-video')}
                disabled={true}
                value={values?.uuid || ''}
                required={required}
                placeholder={t(
                  'activity-document-video.placeholders.uuid-video'
                )}
              />
            </div>
            {values?.file ? (
              <div className='d-flex align-items-center' style={{ gap: '8px' }}>
                <Button
                  onClick={() => setView(true)}
                  variant='secondary'
                  style={{ width: '40px' }}
                >
                  <Icon iconName='Visualization' icSize='semiSmall' />
                </Button>
                <Button
                  variant='secondary'
                  onClick={clearData}
                  style={{ width: '40px' }}
                >
                  <Icon iconName='Cleaner' icSize='semiSmall' />
                </Button>
              </div>
            ) : (
              <div className='d-flex align-items-center' style={{ gap: '8px' }}>
                <Button onClick={handleCameraClick} style={{ width: '40px' }}>
                  <Icon iconName='TakeVideo' icSize='semiSmall' />
                </Button>
                <FileUploader
                  onChange={uploadLocalFile}
                  accept='video/*'
                  variant='secondary'
                  style={{ width: '40px' }}
                />
              </div>
            )}
          </div>
          <Unifree
            label={t('activity-document-video.labels.status')}
            disabled={true}
            value={values?.statusDes || ''}
            required={required}
          />
          <div className='row'>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-video.labels.content-type')}
                disabled={true}
                value={values?.contentType || ''}
                required={required}
                placeholder={t(
                  'activity-document-video.placeholders.content-type'
                )}
              />
            </div>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-video.labels.size')}
                disabled={true}
                value={values?.size ? formatFileSize(Number(values.size)) : ''}
                required={required}
                placeholder={t('activity-document-video.placeholders.size')}
              />
            </div>
          </div>
          <div className='w-100 d-flex justify-content-center align-items-center'>
            {values?.file ? (
              <Player
                source={values.file}
                type={values?.contentType || 'video/mp4'}
              />
            ) : (
              <div className='w-100 webcam-no-image rounded'>
                {t('activity-document-video.no-video')}
              </div>
            )}
          </div>
        </div>
      )}
      {isOpenModal && (
        <WebcamStreamCapture onSuccess={captureVideo} onCancel={closeWebcam} />
      )}
      {view && values?.file && (
        <VideoView
          video={values.file}
          videoType={values?.contentType || 'video/mp4'}
          onCancel={() => setView(false)}
        />
      )}
      <PermissionDeniedCard
        isOpen={permissionDenied}
        onClose={clearPermissionDenied}
        title={permissionDeniedTitle}
      />
    </>
  )
}

export default ActivityDocumentVideo
